import styled from "styled-components";

import { lgToneInk } from "manager/components/app-summary/AppListV2/StatusIcon";
import { COLORS } from "shared/config/constants";

import {
  PanelPrimaryButton,
  VERO1_ACCENT,
  VERO1_FOCUS,
  VERO1_LINK,
  VERO1_MUTED,
} from "./styled";

/**
 * The picked option wears its verdict's color, not the neutral accent — the
 * exact values canvas 11.1–11.5 draws for each: a 1.5px border, a tinted
 * ground, a thick-ringed radio, and the verdict word in the deepest shade.
 *
 * Cancel is never drawn selected (it sits below the rule, off the verdict
 * axis), so its selected state extends its own resting greys rather than
 * borrowing a verdict's color.
 */
export type OptionTone = "approve" | "conditional" | "decline" | "cancel";

const OPTION_TONES: Record<
  OptionTone,
  { border: string; background: string; radio: string; word: string }
> = {
  approve: {
    border: "#56c79a",
    background: "#f3fbf7",
    radio: "#03ab70",
    word: "#03684a",
  },
  conditional: {
    border: "#f5a623",
    background: "#fff6ea",
    radio: "#b35500",
    word: "#b35500",
  },
  decline: {
    border: "#e26982",
    background: "#fdf3f5",
    radio: "#b92443",
    word: "#b92443",
  },
  cancel: {
    border: "#c6c2d4",
    background: "#fbfbfd",
    radio: "#4a4566",
    word: "#4a4566",
  },
};

export const BackLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${VERO1_ACCENT};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${VERO1_MUTED};
  margin: 16px 0 3px;
`;

/** The name picker sits under the chosen option, not inside it — OptionCard
 *  is a button, and a select nested in one is neither valid nor clickable.
 *  It is styled to butt flush against that card so the two read as one
 *  result: the card's own border colours, no gap, bottom radius only. */
export const OptionLabelPicker = styled.div<{ $tone?: OptionTone }>`
  margin: 0 0 6px;
  padding: 9px 12.5px 11px;
  border: 1.5px solid ${({ $tone }) => OPTION_TONES[$tone ?? "cancel"].border};
  border-top: 0;
  border-radius: 0 0 3px 3px;
  background: ${({ $tone }) => OPTION_TONES[$tone ?? "cancel"].background};
`;

export const OptionLabelHeading = styled.div<{ $tone?: OptionTone }>`
  margin-bottom: 5px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $tone }) => OPTION_TONES[$tone ?? "cancel"].word};
`;

/**
 * Canvas 11.1: one baseline row per option — radio, the verdict word in a
 * fixed column, and its description. Compact rows 6px apart read as a single
 * verdict axis rather than a stack of boxes.
 *
 * Lumen's tag sits ABOVE that row rather than at its right edge: at 360px the
 * drawn right-edge placement either crushes the description into a few
 * characters or wraps the tag to a line of its own below the words, where it
 * reads as detached. On top it labels the whole card, and the radio row below
 * it stays identical on every option.
 *
 * The selected card thickens to 1.5px and gives back the half-pixel in
 * padding, so picking down the axis never nudges the rows.
 */
export const OptionCard = styled.button<{
  $selected?: boolean;
  $tone?: OptionTone;
  $attached?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 7px;
  width: 100%;
  text-align: left;
  padding: ${({ $selected }) => ($selected ? "10.5px 12.5px" : "11px 13px")};
  margin-bottom: ${({ $attached }) => ($attached ? "0" : "6px")};
  border-radius: 3px;
  cursor: pointer;
  background: ${({ $selected, $tone }) =>
    $selected ? OPTION_TONES[$tone ?? "cancel"].background : COLORS.white};
  border: ${({ $selected }) => ($selected ? "1.5px" : "1px")}
    ${({ $tone }) => ($tone === "cancel" ? "dashed" : "solid")}
    ${({ $selected, $tone }) =>
      $selected
        ? OPTION_TONES[$tone ?? "cancel"].border
        : $tone === "cancel"
        ? "#c6c2d4"
        : COLORS.borders};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

/** Radio + text, the drawn baseline row (canvas 11.1). */
/** The step's meta line (spec: "Chase Mariott · 9 of 11 items assessed").
 *  The platform's SmallText is 12px at the default weight and colour, which
 *  read as unstyled beside the panel's own scale — the Deal review tab already
 *  carries this exact treatment for the same line. */
export const ReportMetaLine = styled.span`
  display: block;
  font: 300 11.5px "Work Sans", sans-serif;
  color: ${VERO1_MUTED};
`;

export const OptionMain = styled.span`
  display: flex;
  align-items: flex-start;
  gap: 11px;
`;

/** The drawn radio: a hollow ring at rest, filled to a thick 4.5px band in
 *  the verdict's colour once chosen — no inner dot, the band IS the mark. */
export const OptionRadio = styled.span<{
  $selected?: boolean;
  $tone?: OptionTone;
}>`
  flex: 0 0 auto;
  width: 15px;
  height: 15px;
  margin-top: 1px;
  border-radius: 50%;
  background: ${COLORS.white};
  border: ${({ $selected, $tone }) =>
    $selected
      ? `4.5px solid ${OPTION_TONES[$tone ?? "cancel"].radio}`
      : "1.5px solid #b7b3c9"};
`;

/** Word and description share one baseline (canvas 11.1) — with the tag
 *  lifted to the card's head, both hold their drawn widths at 360px. */
export const OptionRow = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 11px;
`;

/** The verdict word holds a fixed column so the descriptions line up down
 *  the axis; it deepens to its verdict colour when chosen. */
export const OptionWord = styled.span<{
  $selected?: boolean;
  $tone?: OptionTone;
}>`
  /* 82px fitted Vero's own words (Approve / Decline). A client's configured
     name is longer — "Expired Credit Report" broke onto three lines — so the
     column starts there and grows with the name, wrapping at whole words. */
  flex: 0 1 auto;
  min-width: 82px;
  max-width: 45%;
  overflow-wrap: break-word;
  font-size: 13px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  color: ${({ $selected, $tone }) =>
    $selected
      ? OPTION_TONES[$tone ?? "cancel"].word
      : $tone === "cancel"
      ? "#4a4566"
      : COLORS.primary};
`;

export const OptionDescription = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 11.5px;
  font-weight: 300;
  line-height: 1.4;
  color: ${COLORS.secondary};
`;

/** The prototype closes the flow on a green submit. */
/** The report step closes on the panel's primary control. */
export const SubmitButton = PanelPrimaryButton;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  border: 1px solid ${COLORS.borders};
  border-radius: 2px;
  padding: 9px 11px;
  font: inherit;
  font-weight: 300;
  resize: vertical;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${VERO1_FOCUS};
    box-shadow: 0 0 0 3px rgba(128, 82, 208, 0.18);
  }
`;

export const TextInput = styled.input`
  width: 100%;
  border: 1px solid ${COLORS.borders};
  /* canvas .field: 2px radius, 9px/11px padding, 300 13px */
  border-radius: 2px;
  padding: 9px 11px;
  font: inherit;
  font-weight: 300;

  /* canvas draws the focused field in secondary-500 with a soft halo */
  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${VERO1_FOCUS};
    box-shadow: 0 0 0 3px rgba(128, 82, 208, 0.18);
  }
`;

/** Step-3 sections are boxed cards with a grey header strip (prototype). */
export const ReportCard = styled.section`
  border: 1px solid ${COLORS.borders};
  border-radius: 3px;
  overflow: hidden;
  margin: 16px 0 0;
`;

export const ReportCardHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 9px;
  flex-wrap: wrap;
  padding: 10px 13px;
  background: ${COLORS.lightGray2};
  border-bottom: 1px solid ${COLORS.borders};
  font-size: 12.5px;
  font-weight: 500;
  color: ${COLORS.primary};
`;

export const ReportCardBody = styled.div`
  padding: 3px 13px 12px;
  background: ${COLORS.white};
`;

/** "N items" count on the Summary findings card header. */
export const CountChip = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: 2px;
  font-size: 11px;
  font-weight: 500;
  color: ${COLORS.primary};
  background: ${COLORS.borders};
`;

/** One touched item inside Summary findings — name, diff, findings,
 *  leasing action (spec 11.2); divider-separated, no bullets. */
export const SummaryEntry = styled.div`
  /* canvas 11 (360 redraw): the item name STACKS above its findings rather
     than sitting in a 128px label column — at 360 that gutter left the
     findings too narrow to read */
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 9px 0;
  border-top: 1px solid ${COLORS.borders};

  > span:first-child {
    font-size: 11.5px;
    font-weight: 400;
    line-height: 1.5;
    color: ${COLORS.secondary};
  }

  > div {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
`;

export const SummaryResult = styled.span<{
  $tone?: "pass" | "warn" | "fail" | null;
}>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $tone }) => ($tone ? lgToneInk($tone) : COLORS.secondary)};
`;

/** Findings verbatim — what leasing reads. */
export const SummaryFindings = styled.span`
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.5;
  color: ${COLORS.primary};
`;

/** Leasing action sits under the findings it belongs to (spec 11.2). */
export const SummaryAction = styled.div`
  display: flex;
  gap: 7px;
  align-items: baseline;
  margin-top: 3px;

  > span:last-child {
    flex: 1;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    color: ${COLORS.primary};
  }
`;

export const SummaryFieldLabel = styled.span`
  flex: 0 0 auto;
  font-size: 8.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.secondary};
`;

/** Canvas 11.1: a quiet 500/9.5px tag on the option Lumen chose — square
 *  cornered, so it reads as a note on the row rather than a second pill
 *  competing with the verdict word beside it. */
export const LumenResultBadge = styled.span`
  /* inline at the row's end, exactly as the spec draws it */
  flex: 0 0 auto;
  white-space: nowrap;
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLORS.secondary};
  background: #f2f2f7;
  border-radius: 2px;
  padding: 3px 7px;
`;

/**
 * Canvas conflict chrome (§9 / §11): a rose frame headed by a tinted strip —
 * circle-alert, wine heading, hairline dividers. The row conflict paints the
 * editor card itself; the submit conflict stands alone as this frame.
 */
export const ConflictFrame = styled.div<{ $neutral?: boolean }>`
  border: 1px solid ${({ $neutral }) => ($neutral ? COLORS.borders : "#e26982")};
  border-radius: 4px;
  overflow: hidden;
  background: ${COLORS.white};
  box-shadow: ${({ $neutral }) =>
    $neutral
      ? "0 4px 16px -8px rgba(126, 153, 168, 0.45)"
      : "0 4px 16px -8px rgba(226, 105, 130, 0.5)"};
  /* room above so the report's context line reads as its own sentence,
     not as part of the alarm */
  margin: 12px 0 16px;
`;

export const ConflictStrip = styled.div<{
  $bleed?: boolean;
  $neutral?: boolean;
}>`
  display: flex;
  gap: 9px;
  align-items: center;
  padding: 11px 13px;
  background: ${({ $neutral }) => ($neutral ? COLORS.lightGray2 : "#fdf3f5")};
  border-bottom: 1px solid
    ${({ $neutral }) => ($neutral ? COLORS.borders : "#f2cbd4")};
  ${({ $bleed }) => ($bleed ? "margin: -14px -14px 14px;" : "")}

  svg {
    flex: 0 0 auto;
    margin-top: 1px;
  }
`;

export const ConflictHeading = styled.div<{ $neutral?: boolean }>`
  font-size: 12.5px;
  font-weight: 500;
  color: ${({ $neutral }) => ($neutral ? COLORS.secondary : "#8e1b34")};
`;

export const ConflictCopy = styled.p`
  margin: 3px 0 0;
  font-size: 11.5px;
  font-weight: 300;
  line-height: 1.5;
  color: ${COLORS.primary};

  b {
    font-weight: 500;
  }
`;

/** The retry button inside an error strip — spaced below the copy, so the
 *  strip reads heading → copy → action like the drawn conflict banner. */
export const ConflictStripActions = styled.div`
  margin-top: 9px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const ConflictRowsList = styled.div`
  padding: 11px 13px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ConflictActionsBar = styled.div`
  padding: 11px 13px;
  border-top: 1px solid #f2cbd4;
  background: #fafafc;
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

export const ConflictButtonRow = styled.div`
  display: flex;
  gap: 8px;

  /* the shared Button wraps its button element — size the wrapper, then
     let the control fill it */
  > * {
    flex: 1 1 0;
    min-width: 0;
  }

  button {
    width: 100%;
    height: 34px;
    padding: 0 12px;
    font-size: 12.5px;
  }
`;

export const ConflictFootnote = styled.p`
  margin: 0;
  font-size: 10.5px;
  font-weight: 300;
  line-height: 1.4;
  color: ${VERO1_MUTED};
`;

export const ConflictActions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 6px;
`;

/** The conflict action bar, matching the spec canvas exactly: a rule above
 *  it, the buttons in a wrapping row (gap 8), the footnote stacked below
 *  (column gap 9). */
export const ConflictBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  border-top: 1px solid ${COLORS.borders};
  padding-top: 13px;
  margin-top: 14px;
`;

export const ConflictButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

/** One button, the spec's `.btnk` system: a shared base with pri / ghost /
 *  link variants and a height prop (36px default; 34px in the conflict bar
 *  and the Done row, per canvas). `pri` is ink (primary-400) as the spec's
 *  `.btnk.pri`; the violet accent (secondary-600) is `accent` — the conflict
 *  "Try again" and Done both draw it. */
export const Btnk = styled.button<{
  $variant?: "pri" | "accent" | "ghost" | "link";
  $height?: number;
}>`
  font: 500 13px "Work Sans", -apple-system, sans-serif;
  height: ${({ $height }) => $height ?? 36}px;
  border-radius: 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;

  ${({ $variant = "pri" }) => {
    if ($variant === "link") {
      return `
        padding: 0;
        border: none;
        background: none;
        color: ${VERO1_LINK};
        &:hover { color: ${COLORS.primary}; }
      `;
    }
    const fill = {
      pri: `background: ${COLORS.primary}; color: #fff; border: 1px solid transparent;`,
      accent: `background: ${VERO1_ACCENT}; color: #fff; border: 1px solid transparent;`,
      ghost: `background: ${COLORS.white}; color: ${COLORS.primary}; border: 1px solid ${COLORS.primary};`,
    }[$variant];
    return `
      padding: 0 16px;
      ${fill}
    `;
  }}

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

/** The Done + save-indicator row (spec canvas 7.x): Done on the left, the
 *  status text immediately to its right, gap 12 — not pushed apart. */
export const DoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/** "Saved as you type · 11:42 AM" and its sibling states — 300/11px muted. */
/** The retry affordance in the failed status line — a real button (the bare
 *  word was a dead label), accent so it reads as the recovery action. */
export const SaveRetryLink = styled.button`
  font: 600 11px "Work Sans", -apple-system, sans-serif;
  background: none;
  border: none;
  padding: 0;
  color: ${VERO1_ACCENT};
  cursor: pointer;
  text-decoration: underline;
`;

/** The retry-in-progress line on a collapsed row: no card — just the spinner
 *  and "Saving…" on one baseline-aligned line, in the same muted 300/11px as
 *  the resting save status, so it reads as a transient note, not an alert. */
export const SavingLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  font: 300 11px "Work Sans", -apple-system, sans-serif;
  color: ${COLORS.secondary};

  svg {
    flex: 0 0 auto;
  }
`;

export const SaveStatus = styled.span<{ $failed?: boolean }>`
  font: ${({ $failed }) => ($failed ? 500 : 300)} 11px "Work Sans",
    -apple-system, sans-serif;
  color: ${({ $failed }) => ($failed ? COLORS.failedRed : COLORS.secondary)};
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

export const TheirVersion = styled.div`
  margin: 8px 0 4px;
  padding: 8px 10px;
  border-left: 2px solid ${COLORS.borders};
  background: ${COLORS.lightGray};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ConflictAction = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.secondary};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${COLORS.primary};
  }
`;

/** The drawn cancel-reason picker: selectable chips in a wrapped row. The
 *  radio input stays in the tree for keyboard and AT — the label IS the chip. */
export const ReasonChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const ReasonChip = styled.label<{ $selected?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  /* constant box: the selected border thickens by what the padding gives
     back on both axes, and the hidden bold spacer reserves the selected
     width — selecting a chip never reflows its neighbours */
  border: ${({ $selected }) =>
    $selected
      ? `1.5px solid ${COLORS.primary}`
      : `1px solid ${COLORS.borders}`};
  border-radius: 2px;
  padding: ${({ $selected }) => ($selected ? "6.5px 10.5px" : "7px 11px")};
  font-size: 12px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  color: ${({ $selected }) => ($selected ? COLORS.primary : COLORS.secondary)};
  background: ${({ $selected }) => ($selected ? "#f7f7fb" : COLORS.white)};
  cursor: pointer;

  &::after {
    content: attr(data-label);
    height: 0;
    overflow: hidden;
    visibility: hidden;
    font-weight: 600;
  }

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
  }

  /* keyboard focus only — a mouse pick already shows itself as the
     selected chip, and a focus ring there reads as a second border */
  &:has(input:focus-visible) {
    outline: 2px solid ${COLORS.primary};
    outline-offset: 1px;
  }
`;

/**
 * Canvas 11.1: the step-3 footer is one row pinned under the content —
 * reassurance left, the single forward action right, on a faint ground.
 * The action is the panel's dark primary, not a green button floated to the
 * right of the body.
 */
export const ReportFooter = styled.div`
  margin-top: 16px;
  padding: 13px 0 0;
  border-top: 1px solid ${COLORS.borders};
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const ReportFooterNote = styled.span`
  flex: 1 1 180px;
  min-width: 0;
  font-size: 11.5px;
  font-weight: 300;
  line-height: 1.4;
  color: ${COLORS.secondary};
`;

/** Canvas 11.1: the summary card closes on a rule carrying the edit hint and
 *  a quiet link back to the checklist. */
export const SummaryCardFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  margin-top: 2px;
  padding-top: 10px;
  border-top: 1px solid ${COLORS.borders};

  > span:first-child {
    flex: 1 1 160px;
    min-width: 0;
    font-size: 11px;
    font-weight: 300;
    color: ${COLORS.secondary};
  }
`;
